import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'
import { Button, Flex, Heading, Text } from '@buffer-mono/popcorn'

import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import styles from './TakeawaysGetStarted.module.css'

const CREATE_POST_CTA = 'publish-insights-takeawaysEmptyState-createPost-1'

/** Sample takeaways, drawn rather than rendered. Decorative only. */
const ILLUSTRATION_URL =
  'https://buffer-publish.s3.us-east-1.amazonaws.com/images/insights/takeaways-cards.svg'

interface TakeawaysGetStartedProps {
  /**
   * Channel to preselect in the composer. Omit where the page has no single
   * channel in scope; the composer then opens with nothing selected.
   */
  channelId?: string
  /** When false, hides the CTA. The copy still reads correctly without it. */
  canCreatePosts: boolean
}

/**
 * Stands in for the Takeaways section on an account that has never published.
 * There is nothing to analyse yet, so the section describes what Takeaways will
 * do and points at the one action that starts them.
 */
export function TakeawaysGetStarted({
  channelId,
  canCreatePosts,
}: TakeawaysGetStartedProps): JSX.Element {
  const { createNewPostInComposer } = usePostComposer()
  const commonTrackingProperties = useCommonTrackingProperties()
  const [illustrationLoaded, setIllustrationLoaded] = useState(false)

  const handleCreatePost = useCallback((): void => {
    BufferTracker.cTAClicked({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      cta: CREATE_POST_CTA,
      upgradePathName: null,
      ...commonTrackingProperties,
    })
    void createNewPostInComposer({
      cta: CREATE_POST_CTA,
      ...(channelId != null && { channels: [channelId] }),
    })
  }, [channelId, createNewPostInComposer, commonTrackingProperties])

  return (
    <Flex as="section" direction="column" className={styles.panel}>
      <Flex direction="column" gap="md" className={styles.copy}>
        <Flex direction="column" gap="xs">
          <Heading as="h2" size="large">
            A Path to Growth with Weekly Takeaways
          </Heading>
          <Text as="p" color="subtle">
            The more you post, the better your takeaways. Share a few posts and
            we&apos;ll start showing you weekly insights to help you grow.
          </Text>
        </Flex>
        {canCreatePosts && (
          <Button type="button" onClick={handleCreatePost}>
            Create Post
          </Button>
        )}
      </Flex>
      <div
        className={clsx(
          styles.illustration,
          illustrationLoaded && styles.illustrationLoaded,
        )}
      >
        <img
          alt=""
          className={styles.illustrationImage}
          decoding="async"
          onLoad={(): void => setIllustrationLoaded(true)}
          src={ILLUSTRATION_URL}
        />
      </div>
    </Flex>
  )
}
